import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Phone, UserPlus, MapPin, Building2, TreePine, Home, AlertTriangle, Briefcase, CheckCircle2, Loader2, X, History } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import LocationPreview from "@/components/maps/LocationPreview";

const DRAFT_KEY = "acquigine:intake-draft:v1";
const ZIP_LOOKUP_URL = "https://api.zippopotam.us/us/"; // free, no API key, US ZIPs only

// ---------------------------------------------------------------------------
// Dropdown option sets (mirror backend Literal enums)
// ---------------------------------------------------------------------------

const ASSET_TYPES = [
  { value: "business", label: "Business / Operating Company", icon: Briefcase },
  { value: "multifamily", label: "Multifamily / Apartment", icon: Home },
  { value: "land", label: "Land / Development Site", icon: TreePine },
];

// Pipeline stages ,  mirror models.PipelineStage
const STAGES = [
  { value: "", label: "Not yet placed in pipeline" },
  { value: "new_lead", label: "New Lead" },
  { value: "contact_attempted", label: "Contact Attempted" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "negotiating", label: "Negotiating" },
  { value: "loi_sent", label: "LOI Sent" },
  { value: "under_contract", label: "Under Contract" },
  { value: "due_diligence", label: "Due Diligence" },
  { value: "closed", label: "Closed" },
  { value: "lost", label: "Lost" },
];

const US_STATES = [
  "", "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY",
  "LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH",
  "OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

// Common zoning codes encountered during intake
const ZONING_CODES = [
  "", "R-1 (Single Family)", "R-2 (Two Family)", "R-3 (Multifamily Low Density)",
  "R-4 (Multifamily Mid Density)", "R-5 (Multifamily High Density)",
  "C-1 (Neighborhood Commercial)", "C-2 (General Commercial)", "C-3 (Central Commercial)",
  "M-1 (Light Industrial)", "M-2 (Heavy Industrial)", "MX (Mixed Use)",
  "AG (Agricultural)", "PUD (Planned Unit Development)", "Other",
];

const UTILITIES_OPTIONS = [
  "", "All utilities available", "Water + Sewer at site", "Water only",
  "Septic + Well", "Electric only", "No utilities (raw)", "Partial / to be verified",
];

const DEVELOPMENT_POTENTIAL_OPTIONS = [
  "", "Single-family residential", "Multifamily / apartments", "Mixed-use",
  "Commercial / retail", "Industrial / warehouse", "Subdivision", "Hold (land bank)", "Other",
];

const INDUSTRIES = [
  "", "Service / Professional", "Retail", "Restaurant / Food Service", "Manufacturing",
  "Distribution / Wholesale", "Healthcare", "Construction / Trades", "E-Commerce",
  "Auto / Repair", "Hospitality", "Education / Childcare", "Real Estate Services",
  "Technology / SaaS", "Logistics", "Other",
];

const SOURCE_OPTIONS = [
  { value: "manual", label: "Manual intake (phone / meeting)" },
  { value: "referral", label: "Referral" },
  { value: "cold_call", label: "Cold call" },
  { value: "direct_mail", label: "Direct mail response" },
  { value: "broker", label: "Broker submission" },
  { value: "import", label: "Imported (CSV / webhook)" },
];

// ---------------------------------------------------------------------------
// Small form helpers
// ---------------------------------------------------------------------------

const inputCls = "w-full bg-[#0A0D12] border border-white/10 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/30 rounded-sm px-3 py-2 text-sm text-slate-100 placeholder-slate-600 outline-none transition-colors";
const labelCls = "block text-[11px] uppercase tracking-widest text-slate-500 mb-1.5";

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-emerald-400 ml-0.5">*</span>}</label>
      {children}
      {hint && <div className="text-[10px] text-slate-600 mt-1">{hint}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section
      className="bg-[#0A0D12] border border-white/8 rounded-sm overflow-hidden"
      data-testid={testid}
    >
      <header className="flex items-center gap-2 px-5 py-3 border-b border-white/8 bg-white/[0.02]">
        {Icon && <Icon className="h-4 w-4 text-emerald-400" />}
        <h2 className="text-sm font-semibold tracking-tight text-slate-200">{title}</h2>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Initial form state
// ---------------------------------------------------------------------------

function emptyForm() {
  return {
    asset_type: "business",
    title: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    asking_price: "",
    listing_url: "",
    description: "",
    source: "manual",
    // business
    industry: "",
    revenue: "",
    cash_flow: "",
    ebitda: "",
    employees: "",
    // multifamily
    units: "",
    noi: "",
    occupancy: "",
    cap_rate: "",
    year_built: "",
    // land
    acreage: "",
    zoning: "",
    utilities: "",
    development_potential: "",
    county: "",
    // owner
    owner_name: "",
    owner_phone: "",
    owner_email: "",
    owner_address: "",
    // broker
    broker_name: "",
    broker_phone: "",
    broker_email: "",
    // market / distress
    days_on_market: "",
    price_reductions: "",
    absentee_owner: false,
    tax_delinquent: false,
    deferred_maintenance: false,
    // ownership / parcel
    years_owned: "",
    assessed_value: "",
    lien_flag: false,
    parcel_id: "",
    // seller financing
    seller_financing_flag: false,
    // pipeline
    stage: "",
    // geo (set by LocationPreview after geocoding or pin drag)
    lat: null,
    lng: null,
  };
}

// Coerce string inputs to the types the backend expects
function buildPayload(f) {
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const intNum = (v) => (v === "" || v == null ? 0 : parseInt(v, 10) || 0);
  const p = {
    asset_type: f.asset_type,
    title: f.title.trim(),
    address: f.address.trim(),
    city: f.city.trim(),
    state: f.state,
    zip_code: f.zip_code.trim(),
    asking_price: num(f.asking_price) ?? 0,
    listing_url: f.listing_url.trim(),
    description: f.description.trim(),
    source: f.source,
    days_on_market: intNum(f.days_on_market),
    price_reductions: intNum(f.price_reductions),
    absentee_owner: !!f.absentee_owner,
    tax_delinquent: !!f.tax_delinquent,
    deferred_maintenance: !!f.deferred_maintenance,
    lien_flag: !!f.lien_flag,
    seller_financing_flag: !!f.seller_financing_flag,
  };
  if (f.years_owned !== "") p.years_owned = parseInt(f.years_owned, 10) || null;
  if (f.assessed_value !== "") p.assessed_value = num(f.assessed_value);
  if (f.parcel_id?.trim()) p.parcel_id = f.parcel_id.trim();
  if (f.stage) p.stage = f.stage;
  if (f.asset_type === "business") {
    if (f.industry) p.industry = f.industry;
    if (f.revenue !== "") p.revenue = num(f.revenue);
    if (f.cash_flow !== "") p.cash_flow = num(f.cash_flow);
    if (f.ebitda !== "") p.ebitda = num(f.ebitda);
    if (f.employees !== "") p.employees = parseInt(f.employees, 10) || null;
  }
  if (f.asset_type === "multifamily") {
    if (f.units !== "") p.units = parseInt(f.units, 10) || null;
    if (f.noi !== "") p.noi = num(f.noi);
    if (f.occupancy !== "") p.occupancy = num(f.occupancy);
    if (f.cap_rate !== "") p.cap_rate = num(f.cap_rate);
    if (f.year_built !== "") p.year_built = parseInt(f.year_built, 10) || null;
  }
  if (f.asset_type === "land") {
    if (f.acreage !== "") p.acreage = num(f.acreage);
    if (f.zoning) p.zoning = f.zoning;
    if (f.utilities) p.utilities = f.utilities;
    if (f.development_potential) p.development_potential = f.development_potential;
    if (f.county) p.county = f.county.trim();
  }
  // Owner / broker
  for (const k of ["owner_name", "owner_phone", "owner_email", "owner_address", "broker_name", "broker_phone", "broker_email"]) {
    if (f[k]?.trim()) p[k] = f[k].trim();
  }
  // Geo (set by LocationPreview after geocoding or pin drag)
  if (Number.isFinite(f.lat) && Number.isFinite(f.lng)) {
    p.lat = f.lat;
    p.lng = f.lng;
  }
  return p;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ProspectIntake() {
  const navigate = useNavigate();
  const [f, setF] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  // -------- Auto-save draft to localStorage --------
  const [draftStatus, setDraftStatus] = useState("idle"); // idle | saving | saved
  const [hasDraft, setHasDraft] = useState(false);        // banner: a draft exists from a prior session
  const [draftMeta, setDraftMeta] = useState(null);       // { savedAt: ISO }
  const saveTimer = useRef(null);
  const hydrated = useRef(false);

  // On mount: check for an existing draft and offer to restore it.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.form && parsed?.savedAt) {
          setHasDraft(true);
          setDraftMeta({ savedAt: parsed.savedAt });
        }
      }
    } catch { /* ignore corrupt draft */ }
    hydrated.current = true;
  }, []);

  // Debounced auto-save on form change. Skip the write when the form is
  // effectively empty (e.g. right after a Reset) so we don't leave behind
  // a "draft" that's just default values.
  useEffect(() => {
    if (!hydrated.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);

    const isEmpty = (
      !f.title && !f.address && !f.city && !f.state && !f.zip_code &&
      !f.asking_price && !f.description && !f.listing_url &&
      !f.owner_name && !f.owner_phone && !f.owner_email &&
      !f.broker_name && !f.broker_phone && !f.broker_email &&
      !f.years_owned && !f.assessed_value && !f.parcel_id &&
      !f.lien_flag && !f.absentee_owner && !f.tax_delinquent && !f.deferred_maintenance
    );
    if (isEmpty) {
      // Form is empty (initial mount or after Reset). Don't write a draft,
      // but DON'T destroy any existing draft either ,  Discard/Reset/successful
      // save are the only paths that should delete it. This lets users click
      // "Restore draft" after a reload without us having pre-emptively wiped it.
      return;
    }

    setDraftStatus("saving");
    saveTimer.current = setTimeout(() => {
      try {
        const payload = { form: f, savedAt: new Date().toISOString() };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
        setDraftMeta({ savedAt: payload.savedAt });
        setDraftStatus("saved");
      } catch {
        setDraftStatus("idle");
      }
    }, 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [f]);

  const restoreDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.form) {
        setF({ ...emptyForm(), ...parsed.form });
        setHasDraft(false);
        toast.success("Draft restored.");
      }
    } catch {
      toast.error("Couldn't restore draft.");
    }
  };

  const discardDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setHasDraft(false);
    setDraftMeta(null);
    setDraftStatus("idle");
  };

  // -------- ZIP -> city/state autofill (free, no API key) --------
  const [zipLookup, setZipLookup] = useState({ status: "idle", lastZip: "" });
  // BLOCK 81 ,  ZIP -> County via backend HUD crosswalk. When a ZIP spans
  // more than one county we NEVER guess: county stays blank and we surface
  // the real options for the operator to pick (explicit unknown > silent guess).
  const [zipCounty, setZipCounty] = useState({ status: "idle", options: [] });

  useEffect(() => {
    const zip = (f.zip_code || "").trim();
    if (!/^\d{5}$/.test(zip)) {
      if (zipLookup.status !== "idle") setZipLookup({ status: "idle", lastZip: "" });
      if (zipCounty.status !== "idle") setZipCounty({ status: "idle", options: [] });
      return;
    }
    if (zip === zipLookup.lastZip) return;
    const ac = new AbortController();
    setZipLookup({ status: "loading", lastZip: zip });
    setZipCounty({ status: "loading", options: [] });
    // Clear county ONCE up front so the HUD result below is the single writer
    // (avoids a race where the slower zippopotam handler blanks it afterwards).
    setF((prev) => ({ ...prev, county: "" }));
    fetch(`${ZIP_LOOKUP_URL}${zip}`, { signal: ac.signal })
      .then((r) => {
        if (!r.ok) throw new Error("not_found");
        return r.json();
      })
      .then((data) => {
        const place = data?.places?.[0];
        if (!place) throw new Error("no_place");
        const city = place["place name"];
        const state = place["state abbreviation"];
        setF((prev) => ({
          ...prev,
          city,
          state,
          lat: null,
          lng: null,
        }));
        setZipLookup({ status: "ok", lastZip: zip });
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setZipLookup({ status: "err", lastZip: zip });
      });
    // BLOCK 81 ,  resolve County independently (HUD crosswalk, backend).
    api.get("/geo/zip-county", { params: { zip } })
      .then((r) => {
        const d = r.data || {};
        if (d.county) {
          setF((prev) => ({ ...prev, county: d.county }));
          setZipCounty({ status: "single", options: [d.county] });
        } else if (d.ambiguous) {
          setZipCounty({ status: "ambiguous", options: d.options || [] });
        } else {
          setZipCounty({ status: "none", options: [] });
        }
      })
      .catch(() => setZipCounty({ status: "err", options: [] }));
    return () => ac.abort();
  }, [f.zip_code]);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const setCoords = ({ lat, lng }) => setF((prev) => ({ ...prev, lat, lng }));

  const validate = () => {
    if (!f.title.trim()) return "Title is required.";
    if (!f.address.trim()) return "Street address is required.";
    if (!f.city.trim()) return "City is required.";
    if (!f.state) return "State is required.";
    if (f.owner_email && !/^\S+@\S+\.\S+$/.test(f.owner_email)) return "Owner email is invalid.";
    if (f.broker_email && !/^\S+@\S+\.\S+$/.test(f.broker_email)) return "Broker email is invalid.";
    return null;
  };

  const onSubmit = async (e, andAnother) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const r = await api.post("/opportunities", buildPayload(f));
      toast.success(`Saved: ${r.data?.title || "opportunity"}`);
      // Successful save: discard the draft so the form doesn't recover stale data later.
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setHasDraft(false);
      setDraftMeta(null);
      if (andAnother) {
        setF({ ...emptyForm(), asset_type: f.asset_type, source: f.source });
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate(`/app/opportunities/${r.data.oid}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => onSubmit(e, false)}
      className="max-w-5xl mx-auto py-8 px-4 space-y-6"
      data-testid="prospect-intake-page"
    >
      {/* Restore-draft banner (shows when a prior session left a draft behind) */}
      {hasDraft && (
        <div
          className="ae-card flex items-center justify-between gap-4 px-5 py-3 bg-emerald-500/5 border-emerald-500/30"
          data-testid="draft-restore-banner"
        >
          <div className="flex items-center gap-2 text-sm text-emerald-200">
            <History className="h-4 w-4" />
            <span>
              A draft from {draftMeta?.savedAt ? fmtDateTime(draftMeta.savedAt) : "a prior session"} was found.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={restoreDraft}
              className="text-xs px-3 py-1.5 rounded-sm bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition"
              data-testid="draft-restore-btn"
            >
              Restore draft
            </button>
            <button
              type="button"
              onClick={discardDraft}
              className="text-xs px-3 py-1.5 rounded-sm border border-white/10 text-slate-400 hover:text-slate-200 transition"
              data-testid="draft-discard-btn"
            >
              <X className="h-3 w-3 inline -mt-0.5 mr-1" />
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
            <Phone className="h-3 w-3" /> Live intake
          </div>
          <h1 className="font-heading text-3xl tracking-tight mt-1">Prospect Intake</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Capture information while on a call with a seller, broker, or owner.
            Required fields are marked with <span className="text-emerald-400">*</span>. Asset-specific sections appear based on the asset type.
          </p>
          {draftStatus !== "idle" && (
            <div
              className="text-[10px] uppercase tracking-widest text-slate-500 mt-2 flex items-center gap-1.5"
              data-testid="draft-status"
            >
              {draftStatus === "saving" ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Saving draft…</>
              ) : (
                <>
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  Draft auto-saved
                  {draftMeta?.savedAt && (
                    <span className="text-slate-600 ml-1 normal-case tracking-normal">
                      {new Date(draftMeta.savedAt).toLocaleTimeString()}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setF(emptyForm()); discardDraft(); }}
            className="text-xs px-3 py-2 rounded-sm border border-white/10 text-slate-400 hover:text-slate-200 hover:border-white/20 transition"
            data-testid="intake-reset"
          >
            Reset form
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => onSubmit(e, true)}
            className="text-xs px-3 py-2 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition disabled:opacity-50"
            data-testid="intake-save-and-another"
          >
            <UserPlus className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            Save & add another
          </button>
          <button
            type="submit"
            disabled={busy}
            className="text-xs px-4 py-2 rounded-sm bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition disabled:opacity-50"
            data-testid="intake-submit"
          >
            <Save className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
            {busy ? "Saving…" : "Save prospect"}
          </button>
        </div>
      </header>

      {/* Asset basics */}
      <Section title="Asset basics" icon={Building2} testid="section-asset-basics">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Asset type" required>
            <select
              value={f.asset_type}
              onChange={(e) => set("asset_type", e.target.value)}
              className={inputCls}
              data-testid="intake-asset-type"
            >
              {ASSET_TYPES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Lead source">
            <select
              value={f.source}
              onChange={(e) => set("source", e.target.value)}
              className={inputCls}
              data-testid="intake-source"
            >
              {SOURCE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Pipeline stage" hint="Optional. Leave blank to keep this lead out of the pipeline until qualified.">
            <select
              value={f.stage}
              onChange={(e) => set("stage", e.target.value)}
              className={inputCls}
              data-testid="intake-stage"
            >
              {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <div className="md:col-span-3">
            <Field label="Title" required hint='Brief descriptor, e.g. "24-unit Cleveland multifamily" or "Phoenix HVAC company"'>
              <input
                value={f.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. 24-unit Cleveland multifamily"
                className={inputCls}
                data-testid="intake-title"
              />
            </Field>
          </div>
          <Field label="Asking price (USD)">
            <input
              type="number"
              min="0"
              step="1000"
              value={f.asking_price}
              onChange={(e) => set("asking_price", e.target.value)}
              placeholder="0"
              className={inputCls}
              data-testid="intake-asking-price"
            />
          </Field>
          <Field label="Days on market">
            <input
              type="number"
              min="0"
              value={f.days_on_market}
              onChange={(e) => set("days_on_market", e.target.value)}
              placeholder="0"
              className={inputCls}
              data-testid="intake-days-on-market"
            />
          </Field>
          <Field label="Price reductions">
            <input
              type="number"
              min="0"
              value={f.price_reductions}
              onChange={(e) => set("price_reductions", e.target.value)}
              placeholder="0"
              className={inputCls}
              data-testid="intake-price-reductions"
            />
          </Field>
          <div className="md:col-span-3">
            <Field label="Listing URL">
              <input
                type="url"
                value={f.listing_url}
                onChange={(e) => set("listing_url", e.target.value)}
                placeholder="https://..."
                className={inputCls}
                data-testid="intake-listing-url"
              />
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="Description / Notes from call">
              <textarea
                rows={4}
                value={f.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Key takeaways from the conversation: seller motivation, timeline, must-haves, deal-breakers, etc."
                className={inputCls + " font-mono text-[12px] leading-relaxed"}
                data-testid="intake-description"
              />
            </Field>
          </div>
        </div>
      </Section>

      {/* Location */}
      <Section title="Location" icon={MapPin} testid="section-location">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-4">
            <Field label="Street address" required>
              <input
                value={f.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="123 Main St"
                className={inputCls}
                data-testid="intake-address"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field
              label="ZIP / Postal"
              hint={
                zipLookup.status === "loading"
                  ? "Looking up city/state…"
                  : zipLookup.status === "ok"
                    ? "City & state auto-filled from ZIP. You can override."
                    : zipLookup.status === "err"
                      ? "ZIP not found. Enter city/state manually."
                      : "Enter a 5-digit US ZIP and we'll auto-fill the city + state."
              }
            >
              <div className="relative">
                <input
                  value={f.zip_code}
                  onChange={(e) => set("zip_code", e.target.value)}
                  placeholder="44114"
                  inputMode="numeric"
                  maxLength={10}
                  className={inputCls + " pr-8"}
                  data-testid="intake-zip"
                />
                {zipLookup.status === "loading" && (
                  <Loader2
                    className="h-4 w-4 absolute right-2.5 top-2.5 text-emerald-400 animate-spin"
                    data-testid="zip-loading"
                  />
                )}
                {zipLookup.status === "ok" && (
                  <CheckCircle2
                    className="h-4 w-4 absolute right-2.5 top-2.5 text-emerald-500"
                    data-testid="zip-success"
                  />
                )}
              </div>
            </Field>
          </div>
          <div className="md:col-span-3">
            <Field label="City" required>
              <input
                value={f.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="Cleveland"
                className={inputCls}
                data-testid="intake-city"
              />
            </Field>
          </div>
          <div className="md:col-span-1">
            <Field label="State" required>
              <select
                value={f.state}
                onChange={(e) => set("state", e.target.value)}
                className={inputCls}
                data-testid="intake-state"
              >
                {US_STATES.map((s) => <option key={s} value={s}>{s || "(none)"}</option>)}
              </select>
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="County">
              <input
                value={f.county}
                onChange={(e) => set("county", e.target.value)}
                placeholder="Cuyahoga"
                className={inputCls}
                data-testid="intake-county"
              />
              {zipCounty.status === "single" && f.county && (
                <div className="text-[11px] text-emerald-400 mt-1" data-testid="zip-county-autofilled">
                  Auto-filled from ZIP
                </div>
              )}
              {zipCounty.status === "ambiguous" && (
                <div className="mt-1.5" data-testid="zip-county-ambiguous">
                  <div className="text-[11px] text-amber-400">
                    This ZIP spans {zipCounty.options.length} counties ,  pick one:
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {zipCounty.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        data-testid={`zip-county-option-${opt}`}
                        onClick={() => { set("county", opt); setZipCounty({ status: "single", options: [opt] }); }}
                        className="text-[11px] px-2 py-1 rounded-sm border border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                      >{opt}</button>
                    ))}
                  </div>
                </div>
              )}
            </Field>
          </div>
        </div>

        {/* Maps preview, debounced auto-geocode on address change */}
        <div className="mt-4">
          <LocationPreview
            address={f.address}
            city={f.city}
            state={f.state}
            zip_code={f.zip_code}
            initialLat={f.lat}
            initialLng={f.lng}
            onCoordsChange={setCoords}
            onCountyResolved={(county) => setF((prev) => ({ ...prev, county }))}
          />
        </div>
      </Section>

      {/* Asset-specific */}
      {f.asset_type === "business" && (
        <Section title="Business details" icon={Briefcase} testid="section-business">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Industry">
              <select
                value={f.industry}
                onChange={(e) => set("industry", e.target.value)}
                className={inputCls}
                data-testid="intake-industry"
              >
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i || "(none)"}</option>)}
              </select>
            </Field>
            <Field label="Annual revenue (USD)">
              <input
                type="number" min="0" step="1000"
                value={f.revenue}
                onChange={(e) => set("revenue", e.target.value)}
                className={inputCls}
                data-testid="intake-revenue"
              />
            </Field>
            <Field label="Cash flow / SDE (USD)">
              <input
                type="number" min="0" step="1000"
                value={f.cash_flow}
                onChange={(e) => set("cash_flow", e.target.value)}
                className={inputCls}
                data-testid="intake-cash-flow"
              />
            </Field>
            <Field label="EBITDA (USD)">
              <input
                type="number" step="1000"
                value={f.ebitda}
                onChange={(e) => set("ebitda", e.target.value)}
                className={inputCls}
                data-testid="intake-ebitda"
              />
            </Field>
            <Field label="Employees">
              <input
                type="number" min="0"
                value={f.employees}
                onChange={(e) => set("employees", e.target.value)}
                className={inputCls}
                data-testid="intake-employees"
              />
            </Field>
          </div>
        </Section>
      )}

      {f.asset_type === "multifamily" && (
        <Section title="Multifamily details" icon={Home} testid="section-multifamily">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Units">
              <input
                type="number" min="0"
                value={f.units}
                onChange={(e) => set("units", e.target.value)}
                className={inputCls}
                data-testid="intake-units"
              />
            </Field>
            <Field label="NOI (USD)">
              <input
                type="number" step="1000"
                value={f.noi}
                onChange={(e) => set("noi", e.target.value)}
                className={inputCls}
                data-testid="intake-noi"
              />
            </Field>
            <Field label="Cap rate (decimal, e.g. 0.075)" hint="Or enter 7.5 ,  both formats accepted">
              <input
                type="number" step="0.001" min="0"
                value={f.cap_rate}
                onChange={(e) => set("cap_rate", e.target.value)}
                className={inputCls}
                data-testid="intake-cap-rate"
              />
            </Field>
            <Field label="Occupancy %" hint="0 to 1 or 0 to 100, e.g. 0.92 or 92">
              <input
                type="number" step="0.01" min="0" max="100"
                value={f.occupancy}
                onChange={(e) => set("occupancy", e.target.value)}
                className={inputCls}
                data-testid="intake-occupancy"
              />
            </Field>
            <Field label="Year built">
              <input
                type="number" min="1800" max="2100"
                value={f.year_built}
                onChange={(e) => set("year_built", e.target.value)}
                className={inputCls}
                data-testid="intake-year-built"
              />
            </Field>
          </div>
        </Section>
      )}

      {f.asset_type === "land" && (
        <Section title="Land details" icon={TreePine} testid="section-land">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Acreage">
              <input
                type="number" min="0" step="0.01"
                value={f.acreage}
                onChange={(e) => set("acreage", e.target.value)}
                className={inputCls}
                data-testid="intake-acreage"
              />
            </Field>
            <Field label="Zoning">
              <select
                value={f.zoning}
                onChange={(e) => set("zoning", e.target.value)}
                className={inputCls}
                data-testid="intake-zoning"
              >
                {ZONING_CODES.map((z) => <option key={z} value={z}>{z || "(none)"}</option>)}
              </select>
            </Field>
            <Field label="Utilities">
              <select
                value={f.utilities}
                onChange={(e) => set("utilities", e.target.value)}
                className={inputCls}
                data-testid="intake-utilities"
              >
                {UTILITIES_OPTIONS.map((u) => <option key={u} value={u}>{u || "(none)"}</option>)}
              </select>
            </Field>
            <div className="md:col-span-3">
              <Field label="Development potential">
                <select
                  value={f.development_potential}
                  onChange={(e) => set("development_potential", e.target.value)}
                  className={inputCls}
                  data-testid="intake-development-potential"
                >
                  {DEVELOPMENT_POTENTIAL_OPTIONS.map((d) => <option key={d} value={d}>{d || "(none)"}</option>)}
                </select>
              </Field>
            </div>
          </div>
        </Section>
      )}

      {/* Owner contact */}
      <Section title="Owner / Seller contact" icon={UserPlus} testid="section-owner">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Owner name">
            <input
              value={f.owner_name}
              onChange={(e) => set("owner_name", e.target.value)}
              className={inputCls}
              data-testid="intake-owner-name"
              autoComplete="off"
            />
          </Field>
          <Field label="Owner phone">
            <input
              type="tel"
              value={f.owner_phone}
              onChange={(e) => set("owner_phone", e.target.value)}
              placeholder="(555) 555-0100"
              className={inputCls}
              data-testid="intake-owner-phone"
            />
          </Field>
          <Field label="Owner email">
            <input
              type="email"
              value={f.owner_email}
              onChange={(e) => set("owner_email", e.target.value)}
              className={inputCls}
              data-testid="intake-owner-email"
              autoComplete="off"
            />
          </Field>
          <Field label="Owner mailing address" hint='Use this when the owner is absentee ,  different from the property address.'>
            <input
              value={f.owner_address}
              onChange={(e) => set("owner_address", e.target.value)}
              className={inputCls}
              data-testid="intake-owner-address"
            />
          </Field>
        </div>
      </Section>

      {/* Broker contact */}
      <Section title="Broker / Listing agent" icon={UserPlus} testid="section-broker">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Broker name">
            <input
              value={f.broker_name}
              onChange={(e) => set("broker_name", e.target.value)}
              className={inputCls}
              data-testid="intake-broker-name"
            />
          </Field>
          <Field label="Broker phone">
            <input
              type="tel"
              value={f.broker_phone}
              onChange={(e) => set("broker_phone", e.target.value)}
              className={inputCls}
              data-testid="intake-broker-phone"
            />
          </Field>
          <Field label="Broker email">
            <input
              type="email"
              value={f.broker_email}
              onChange={(e) => set("broker_email", e.target.value)}
              className={inputCls}
              data-testid="intake-broker-email"
            />
          </Field>
        </div>
      </Section>

      {/* Distress signals */}
      <Section title="Distress & motivation signals" icon={AlertTriangle} testid="section-distress">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/8 hover:border-emerald-500/30 cursor-pointer transition" data-testid="intake-absentee-owner-wrap">
            <input
              type="checkbox"
              checked={f.absentee_owner}
              onChange={(e) => set("absentee_owner", e.target.checked)}
              className="mt-1"
              data-testid="intake-absentee-owner"
            />
            <span className="text-sm">
              <div className="font-medium text-slate-200">Absentee owner</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Owner lives at a different address than the asset.</div>
            </span>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/8 hover:border-emerald-500/30 cursor-pointer transition" data-testid="intake-tax-delinquent-wrap">
            <input
              type="checkbox"
              checked={f.tax_delinquent}
              onChange={(e) => set("tax_delinquent", e.target.checked)}
              className="mt-1"
              data-testid="intake-tax-delinquent"
            />
            <span className="text-sm">
              <div className="font-medium text-slate-200">Tax delinquent</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Past-due property taxes or liens reported.</div>
            </span>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/8 hover:border-emerald-500/30 cursor-pointer transition" data-testid="intake-deferred-maintenance-wrap">
            <input
              type="checkbox"
              checked={f.deferred_maintenance}
              onChange={(e) => set("deferred_maintenance", e.target.checked)}
              className="mt-1"
              data-testid="intake-deferred-maintenance"
            />
            <span className="text-sm">
              <div className="font-medium text-slate-200">Deferred maintenance</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Visible repair backlog or major capex looming.</div>
            </span>
          </label>
        </div>
      </Section>

      {/* Ownership & parcel facts */}
      <Section title="Ownership & parcel facts" icon={Building2} testid="section-ownership-parcel">
        <p className="text-xs text-slate-500 -mt-2 mb-4">
          These directly drive the CF readiness score. Enter what you know now; enrichment will fill the rest.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Years owned" hint="How long has the current owner held this asset?">
            <input
              type="number"
              min="0"
              step="1"
              value={f.years_owned}
              onChange={(e) => set("years_owned", e.target.value)}
              placeholder="e.g. 12"
              className={inputCls}
              data-testid="intake-years-owned"
            />
          </Field>
          <Field label="Assessed value ($)" hint="County-assessed value ,  used as equity proxy.">
            <input
              type="number"
              min="0"
              step="1000"
              value={f.assessed_value}
              onChange={(e) => set("assessed_value", e.target.value)}
              placeholder="e.g. 450000"
              className={inputCls}
              data-testid="intake-assessed-value"
            />
          </Field>
          <Field label="Parcel ID / APN" hint="Assessor parcel number for cross-reference.">
            <input
              type="text"
              value={f.parcel_id}
              onChange={(e) => set("parcel_id", e.target.value)}
              placeholder="e.g. 1234-56-789"
              className={inputCls}
              data-testid="intake-parcel-id"
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/8 hover:border-emerald-500/30 cursor-pointer transition md:col-span-3" data-testid="intake-lien-flag-wrap">
            <input
              type="checkbox"
              checked={f.lien_flag}
              onChange={(e) => set("lien_flag", e.target.checked)}
              className="mt-1"
              data-testid="intake-lien-flag"
            />
            <span className="text-sm">
              <div className="font-medium text-slate-200">Active lien on the asset</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Mechanic, judgment, or other encumbrance on title. Strong motivation signal.</div>
            </span>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/8 hover:border-emerald-500/30 cursor-pointer transition md:col-span-3" data-testid="intake-sf-flag-wrap">
            <input
              type="checkbox"
              checked={f.seller_financing_flag}
              onChange={(e) => set("seller_financing_flag", e.target.checked)}
              className="mt-1"
              data-testid="intake-sf-flag"
            />
            <span className="text-sm">
              <div className="font-medium text-emerald-300">Seller financing offered?</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Owner has explicitly indicated willingness to carry the note. +14 to Receptivity in the score.</div>
            </span>
          </label>
        </div>
      </Section>

      {/* Sticky bottom actions (mobile-friendly) */}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
        <button
          type="button"
          disabled={busy}
          onClick={(e) => onSubmit(e, true)}
          className="text-sm px-4 py-2 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition disabled:opacity-50"
          data-testid="intake-save-and-another-bottom"
        >
          Save & add another
        </button>
        <button
          type="submit"
          disabled={busy}
          className="text-sm px-5 py-2 rounded-sm bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition disabled:opacity-50"
          data-testid="intake-submit-bottom"
        >
          <Save className="h-4 w-4 inline -mt-0.5 mr-1.5" />
          {busy ? "Saving…" : "Save prospect"}
        </button>
      </div>
    </form>
  );
}
